import { InspectionItems } from "./inspection-items";
import { InspectionKanban } from "./inspection-kanban";
import { useEffect, useState, useMemo } from "react";
import KanbanAPI from "../../api/kanban-board/kanban-api-factory";
import { DateTime } from "luxon";
import { useMsal, } from '@azure/msal-react';
import { arrayAny } from "../../utils/arrayUtils";
import { Panel, PanelHeader, PanelBody } from '../panel/panel.jsx';

export const InspectionDetails = ({ containerClassName, propertyDocument, saveProperty }) => {

    const { instance, inProgress, accounts } = useMsal();
    const kanbanAPI = useMemo(() => new KanbanAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const [inspections, setInspections] = useState(null);
    const [fetchInspectionsInProgress, setFetchInspectionsInProgress] = useState(false);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageBeingFetched, setPageBeingFetched] = useState(-1);
    const [selectedProject, setSelectedProject] = useState(null);


    useEffect(() => {
        const fetchData = async () => {
            // Only fetch if we have a property with selectedProject and we're not already fetching
            if (!propertyDocument?.property?.selectedProject || fetchInspectionsInProgress) {
                return;
            }

            // Check if we need to fetch (new page or different project)
            const needsNewData = pageNumber !== pageBeingFetched || propertyDocument?.property.selectedProject !== selectedProject;
            if (!needsNewData) {
                return;
            }

            try {
                setPageBeingFetched(pageNumber);
                setSelectedProject(propertyDocument.property.selectedProject);
                setInspections(null);
                setFetchInspectionsInProgress(true);

                const data = await kanbanAPI.findSubTasks(
                    propertyDocument.property.selectedProject,
                    pageNumber,
                    3,
                    tile => tile.completed && tile.template && tile.template.isInspection,
                    st => st.completed,
                    "desc"
                );
                setInspections(data);
            } catch (error) {
                console.error('Error fetching inspections:', error);
                setInspections({ pagedTasks: [], totalCount: 0 });
            } finally {
                setFetchInspectionsInProgress(false);
            }
        };

        fetchData();
    }, [propertyDocument?.property?.selectedProject, pageNumber, kanbanAPI]);

    const humanizeDate = (d) => {
        return DateTime.fromISO(d).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
    }

    const decrement = () => {
        if (pageNumber > 0) {
            setPageNumber(pageNumber - 1);
        }
        else {
            setPageNumber(0);
        }
    }

    const increment = () => {
        if (pageNumber < numberOfPages() - 1) {
            setPageNumber(pageNumber + 1);
        }
        else {
            setPageNumber(numberOfPages() - 1);
        }
    }

    const numberOfPages = () => {
        return Math.ceil(inspections.totalCount / 3);
    }

    return (
        <div className="row">
            {/* Inspection Kanban Board Section */}
            <div className="col-12 mb-4 p-3 bg-light rounded">
                <InspectionKanban
                    propertyDocument={propertyDocument}
                    saveProperty={saveProperty}
                    onDataChange={(updatedTile) => {

                        // Update existing inspections data if the tile is completed
                        if (updatedTile?.completed && inspections?.pagedTasks) {
                            setInspections(current => {
                                const existingIndex = current.pagedTasks.findIndex(task => task.id === updatedTile.id);
                                if (existingIndex >= 0) {
                                    // Update existing task
                                    const newTasks = [...current.pagedTasks];
                                    newTasks[existingIndex] = updatedTile;
                                    return { ...current, pagedTasks: newTasks };
                                } else {
                                    // Add new completed task
                                    return { 
                                        ...current, 
                                        pagedTasks: [updatedTile, ...current.pagedTasks].slice(0, 3),
                                        totalCount: current.totalCount + 1
                                    };
                                }
                            });
                        }
                    }}
                />
            </div>

            {/* Completed Inspections Table Section */}
            <div className="col-12">
                <Panel>
                    <PanelHeader>
                        Completed Inspections
                    </PanelHeader>
                    <PanelBody>
                        {inspections && arrayAny(inspections.pagedTasks) ?
                            <div key={propertyDocument.property.propertyId} className={containerClassName}>
                                <div className="table-responsive">
                                    <table className="table table-bordered align-middle table-hover">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th nowrap="true">Property</th>
                                                <th nowrap="true">Inspection Date</th>
                                                <th nowrap="true">Title</th>
                                                <th nowrap="true">Completed By</th>
                                                <th nowrap="true">Inspection Items</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(inspections && arrayAny(inspections.pagedTasks) && inspections.pagedTasks.map((insp, i) =>
                                                <tr key={insp.id}>
                                                    <td nowrap="true">{i + 1}</td>
                                                    <td nowrap="true">{propertyDocument.property.name}</td>
                                                    <td nowrap="true">{humanizeDate(insp.completed)}</td>
                                                    <td nowrap="true">{insp.name}</td>
                                                    <td nowrap="true">{insp.completedBy.displayName}</td>
                                                    <td nowrap="true">
                                                        <InspectionItems
                                                            data={insp.subTasks}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div>
                                    <div className="pagination pagination-sm mb-3">
                                        <div onClick={decrement} className={pageNumber < 1 ? "page-item disabled" : "page-item"}><div className="page-link">«</div></div>
                                        {Array.from({ length: numberOfPages() }, (_, i) => i).map(i =>
                                            <div key={`page${i}`} onClick={() => setPageNumber(i)} className={pageNumber === i ? "page-item active" : "page-item"}><div className="page-link">{i + 1}</div></div>
                                        )}
                                        <div onClick={increment} className={pageNumber < numberOfPages() - 1 ? "page-item" : "page-item disabled"}><div className="page-link">»</div></div>
                                    </div>
                                </div>
                            </div>
                            :
                            fetchInspectionsInProgress ?
                                <div className="d-flex justify-content-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div> :
                                <div className="alert alert-info" role="alert">
                                    <i className="fas fa-info-circle me-2"></i>
                                    None to display. Create & Complete at least one Inspection above.
                                </div>
                        }
                    </PanelBody>
                </Panel>
            </div>
        </div>
    );
}